/**
 * Event Log Types for Immutable Chargeback Protection
 * Generated by Copilot
 *
 * Stores all critical events for proving:
 * - User authenticated as themselves
 * - User was physically present at location
 * - User authorized charges
 * - Proper locker/order workflow completion
 */

import { DoorSide } from '../common';

export enum EVENT_CATEGORY {
    // Authentication
    AUTHENTICATION = 'AUTHENTICATION',
    SESSION = 'SESSION',
    MFA = 'MFA',

    // Location & Presence
    LOCATION = 'LOCATION',
    GEOLOCATION = 'GEOLOCATION',
    QR_CODE = 'QR_CODE',
    NFC = 'NFC',
    BIOMETRIC = 'BIOMETRIC',

    // Order Lifecycle
    ORDER = 'ORDER',
    PAYMENT = 'PAYMENT',
    AUTHORIZATION = 'AUTHORIZATION',

    // Locker Operations
    LOCKER = 'LOCKER',
    LOCKER_CUSTOMER = 'LOCKER_CUSTOMER',
    LOCKER_BARTENDER = 'LOCKER_BARTENDER',

    // Pickup & Fulfillment
    PICKUP = 'PICKUP',
    FULFILLMENT = 'FULFILLMENT',

    // Security & Compliance
    SECURITY = 'SECURITY',
}

export enum EVENT_ACTION {
    // Authentication
    LOGIN_ATTEMPT = 'LOGIN_ATTEMPT',
    LOGIN_SUCCESS = 'LOGIN_SUCCESS',
    LOGIN_FAILURE = 'LOGIN_FAILURE',
    LOGOUT = 'LOGOUT',
    MFA_REQUESTED = 'MFA_REQUESTED',
    MFA_VERIFIED = 'MFA_VERIFIED',
    MFA_FAILED = 'MFA_FAILED',
    SESSION_CREATED = 'SESSION_CREATED',
    SESSION_EXPIRED = 'SESSION_EXPIRED',
    SESSION_REVOKED = 'SESSION_REVOKED',

    // Location Detection
    WIFI_CONNECTED = 'WIFI_CONNECTED',
    GEOLOCATION_CAPTURED = 'GEOLOCATION_CAPTURED',
    QR_CODE_SCANNED = 'QR_CODE_SCANNED',
    LOCATION_VERIFIED = 'LOCATION_VERIFIED',
    NFC_TAG_SCANNED = 'NFC_TAG_SCANNED',
    NFC_TAG_INVALID = 'NFC_TAG_INVALID',

    // Order Management
    ORDER_INITIATED = 'ORDER_INITIATED',
    ORDER_CONFIRMED = 'ORDER_CONFIRMED',
    ORDER_ADJUSTED = 'ORDER_ADJUSTED',
    ORDER_PAYMENT_AUTHORIZED = 'ORDER_PAYMENT_AUTHORIZED',
    ORDER_PAYMENT_CAPTURED = 'ORDER_PAYMENT_CAPTURED',
    ORDER_PAYMENT_FAILED = 'ORDER_PAYMENT_FAILED',
    ORDER_READY = 'ORDER_READY',
    ORDER_CANCELED = 'ORDER_CANCELED',
    ORDER_COMPLETED = 'ORDER_COMPLETED',

    // Locker - Customer Side
    LOCKER_UNLOCK_REQUESTED = 'LOCKER_UNLOCK_REQUESTED',
    LOCKER_UNLOCK_APPROVED = 'LOCKER_UNLOCK_APPROVED',
    LOCKER_UNLOCK_DENIED = 'LOCKER_UNLOCK_DENIED',
    LOCKER_DOOR_OPENED = 'LOCKER_DOOR_OPENED',
    LOCKER_DOOR_CLOSED = 'LOCKER_DOOR_CLOSED',
    LOCKER_ITEM_RETRIEVED = 'LOCKER_ITEM_RETRIEVED',
    LOCKER_TIMEOUT = 'LOCKER_TIMEOUT',

    // Locker - Bartender Side
    LOCKER_STOCK_INITIATED = 'LOCKER_STOCK_INITIATED',
    LOCKER_ITEM_LOADED = 'LOCKER_ITEM_LOADED',
    LOCKER_READY_FOR_PICKUP = 'LOCKER_READY_FOR_PICKUP',
    LOCKER_BARTENDER_DOOR_OPENED = 'LOCKER_BARTENDER_DOOR_OPENED',
    LOCKER_BARTENDER_DOOR_CLOSED = 'LOCKER_BARTENDER_DOOR_CLOSED',
    LOCKER_ORDER_TRASHED = 'LOCKER_ORDER_TRASHED',

    // Pickup
    PICKUP_INITIATED = 'PICKUP_INITIATED',
    PICKUP_CONFIRMED = 'PICKUP_CONFIRMED',
    PICKUP_COMPLETED = 'PICKUP_COMPLETED',
    PICKUP_FAILED = 'PICKUP_FAILED',

    // Biometrics
    FACE_CHALLENGE_ISSUED = 'FACE_CHALLENGE_ISSUED',
    FACE_VERIFICATION_PASSED = 'FACE_VERIFICATION_PASSED',
    FACE_VERIFICATION_FAILED = 'FACE_VERIFICATION_FAILED',
    FACE_VERIFICATION_FLAGGED = 'FACE_VERIFICATION_FLAGGED',

    // Security
    SUSPICIOUS_ACTIVITY = 'SUSPICIOUS_ACTIVITY',
    UNAUTHORIZED_ACCESS_ATTEMPT = 'UNAUTHORIZED_ACCESS_ATTEMPT',
    DEVICE_FINGERPRINT_CHANGED = 'DEVICE_FINGERPRINT_CHANGED',
}

export enum EVENT_ACTOR_TYPE {
    CUSTOMER = 'CUSTOMER',
    USER = 'USER',
    SYSTEM = 'SYSTEM',
}

export type MFA = 'sms' | 'email' | 'authenticator';
export type AuthType = 'password' | 'biometric' | 'oauth';
/**
 * Metadata that should be captured for each event
 */
export interface EventMetadata {
    // Client Information
    userAgent?: string;
    ipAddress?: string;
    device?: string;
    deviceId?: string;
    deviceFingerprint?: string;
    appVersion?: string;
    os?: string;
    osVersion?: string;

    // Location Information
    latitude?: number;
    longitude?: number;
    accuracy?: number; // meters
    wifi?: {
        ssid?: string;
        bssid?: string;
        signalStrength?: number;
    };

    // Authentication
    authMethod?: AuthType;
    mfaMethod?: MFA;
    tokenId?: string;
    sessionId?: string;

    // Locker Information
    lockerHardwareId?: string;
    cubbyId?: number;
    doorSide?: DoorSide; // Which side opened
    sensorStatus?: {
        doorOpen?: boolean;
        locked?: boolean;
        timestamp?: string;
    };

    // Order Information
    paymentMethod?: 'stripe' | 'cash' | 'card';
    paymentIntentId?: string;
    chargeId?: string;
    cardLast4?: string;
    cardBrand?: string;

    // Error Handling
    errorCode?: string;
    errorMessage?: string;
    stackTrace?: string;

    // Custom Data
    [key: string]: any;
}

export interface EventLogData {
    // Core fields
    category: EVENT_CATEGORY;
    action: EVENT_ACTION;
    status: 'SUCCESS' | 'FAILURE' | 'PENDING';
    actorType: EVENT_ACTOR_TYPE;

    // Entity References
    // Note: businessId is optional - customers don't belong to a business
    // It's only set when there's order/transaction context
    businessId?: number | null;
    customerId?: number;
    userId?: number;
    orderId?: number;
    lockerId?: number;

    // Event Details
    description?: string;
    metadata?: EventMetadata;

    // Timestamps
    timestamp: string; // ISO 8601
    capturedAt: string; // ISO 8601 when event was logged

    // Immutability Hash
    hash?: string; // SHA256 for tamper detection
}

/**
 * Summary - Events to track for chargeback protection:
 *
 * AUTHENTICATION PROOF:
 * - LOGIN_SUCCESS with device fingerprint + IP + user agent
 * - MFA_VERIFIED (if enabled)
 * - SESSION_CREATED with token details
 *
 * PHYSICAL PRESENCE PROOF:
 * - GEOLOCATION_CAPTURED at order time + pickup time
 * - WIFI_CONNECTED to business WiFi
 * - QR_CODE_SCANNED at location
 *
 * AUTHORIZATION PROOF:
 * - ORDER_INITIATED by customer
 * - ORDER_CONFIRMED with customer confirmation
 * - ORDER_PAYMENT_AUTHORIZED with payment intent
 * - ORDER_PAYMENT_CAPTURED with charge ID
 *
 * LOCKER PROOF (Physical Possession):
 * - LOCKER_UNLOCK_APPROVED for customer
 * - LOCKER_DOOR_OPENED (customer side, timestamped)
 * - LOCKER_ITEM_RETRIEVED
 * - LOCKER_DOOR_CLOSED
 *
 * FULFILLMENT PROOF:
 * - LOCKER_ITEM_LOADED by bartender (timestamp)
 * - LOCKER_READY_FOR_PICKUP
 * - PICKUP_COMPLETED
 */
